/**
 * /admin/$slug/templates — notification templates + channel toggles.
 *
 * Large plan:
 *   - Enable/disable toggle for each template × channel (sms, email)
 *   - Click through to edit template wording
 * Free + small:
 *   - List is shown disabled with an upgrade nudge (show-disable-nudge rule)
 *   - SMS toggles shown but grayed out for small (only 2 SMS types are
 *     always on; wording customisation requires large)
 */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit3, Mail, MessageSquare, Type } from 'lucide-react';
import { Card } from '@bookaburra/ui';
import { PlanGate } from '../components/PlanGate';
import { useDocumentTitle } from '../lib/use-document-title';
import { fetchBookingTenant } from '../booking-flow/api';
import { fetchTenantSettings } from '../booking-flow/tenant-settings-api';
import {
  fetchNotificationSettings,
  saveNotificationSetting,
  type NotificationChannel,
  NotificationSettingsApiError,
} from '../booking-flow/notification-settings-api';

type Channel = 'email_subject' | 'email_body' | 'sms';

interface TemplateRow {
  key: string;
  channels: Array<{ channel: Channel; hasOverride: boolean }>;
}

const KEY_LABELS: Record<string, string> = {
  booking_confirmed: 'Booking confirmed',
  booking_rescheduled: 'Booking rescheduled',
  booking_cancelled: 'Booking cancelled',
  booking_reminder_24h: 'Booking reminder (24h)',
  booking_reminder_1h: 'Booking reminder (1h)',
  booking_pending_approval: 'Booking pending approval (customer)',
  approval_required_staff: 'Booking approval request (to staff)',
  booking_approved: 'Booking approved (customer)',
  booking_rejected: 'Booking rejected (customer)',
  waitlist_joined: 'Waitlist joined',
  waitlist_slot_opened: 'Waitlist slot opened',
  waitlist_removed_by_admin: 'Waitlist removed by admin',
  staff_assignment_changed: 'Staff assignment changed',
  email_verification: 'Email verification',
  team_invite: 'Team invite',
};

/** Template keys that have an SMS notification. email_verification and
 *  team_invite are email-only in practice. */
const KEYS_WITH_SMS = new Set([
  'booking_confirmed',
  'booking_rescheduled',
  'booking_cancelled',
  'booking_reminder_24h',
  'booking_reminder_1h',
  'booking_pending_approval',
  'booking_approved',
  'booking_rejected',
  'waitlist_joined',
  'waitlist_slot_opened',
  'waitlist_removed_by_admin',
  'staff_assignment_changed',
]);

async function fetchTemplates(slug: string): Promise<{ templates: TemplateRow[] }> {
  const r = await fetch(`/api/admin/${encodeURIComponent(slug)}/templates`);
  if (!r.ok) throw new Error('list_failed');
  return r.json();
}

export const Route = createFileRoute('/admin/$slug/templates/')({
  component: TemplatesList,
});

function TemplatesList() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();

  const tenantBrand = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  const tenantQ = useQuery({
    queryKey: ['admin-tenant', slug],
    queryFn: () => fetchTenantSettings(slug),
    staleTime: 60_000,
  });

  useDocumentTitle(
    tenantBrand.data
      ? `Templates · ${tenantBrand.data.tenant.name} Admin`
      : 'Templates',
  );

  const listQ = useQuery({
    queryKey: ['admin-templates', slug],
    queryFn: () => fetchTemplates(slug),
  });

  const settingsQ = useQuery({
    queryKey: ['notification-settings', slug],
    queryFn: () => fetchNotificationSettings(slug),
    staleTime: 30_000,
  });

  const toggleMut = useMutation({
    mutationFn: ({
      templateKey,
      channel,
      enabled,
    }: {
      templateKey: string;
      channel: NotificationChannel;
      enabled: boolean;
    }) => saveNotificationSetting(slug, templateKey, channel, enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notification-settings', slug] });
    },
  });

  const tier = tenantQ.data?.tenant.tier ?? 'free';
  const isLarge = tier === 'large';

  function isEnabled(templateKey: string, channel: NotificationChannel): boolean {
    const row = settingsQ.data?.settings.find(
      (s) => s.templateKey === templateKey && s.channel === channel,
    );
    return row ? row.enabled : true;
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-10 sm:px-12">
      <header className="mb-8">
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
          <Edit3 className="h-7 w-7 text-muted-foreground" />
          <span>Notification templates</span>
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Control which notifications are sent and customise their wording.
          Anything you don&rsquo;t override uses the Bookaburra default.
        </p>
      </header>

      {/* Channel enable/disable toggles — large plan only */}
      <section className="mb-8">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
          Notification channels
        </h2>
        <PlanGate slug={slug} requiredPlan="large-business">
          {listQ.isLoading ? (
            <div className="h-32 animate-pulse rounded-2xl bg-secondary" />
          ) : (
            <div className="space-y-3">
              {(listQ.data?.templates ?? [])
                .filter((row) => KEYS_WITH_SMS.has(row.key))
                .map((row) => (
                  <Card
                    key={row.key}
                    className="flex flex-wrap items-center justify-between gap-4 px-5 py-4"
                  >
                    <div>
                      <p className="text-sm font-medium">
                        {KEY_LABELS[row.key] ?? row.key}
                      </p>
                    </div>
                    <div className="flex items-center gap-6">
                      <ToggleButton
                        label="Email"
                        icon={<Mail className="h-4 w-4" />}
                        enabled={isEnabled(row.key, 'email')}
                        disabled={!isLarge || toggleMut.isPending}
                        onChange={(v) =>
                          toggleMut.mutate({
                            templateKey: row.key,
                            channel: 'email',
                            enabled: v,
                          })
                        }
                      />
                      <ToggleButton
                        label="SMS"
                        icon={<MessageSquare className="h-4 w-4" />}
                        enabled={isEnabled(row.key, 'sms')}
                        disabled={!isLarge || toggleMut.isPending}
                        onChange={(v) =>
                          toggleMut.mutate({
                            templateKey: row.key,
                            channel: 'sms',
                            enabled: v,
                          })
                        }
                      />
                    </div>
                  </Card>
                ))}
              {toggleMut.error ? (
                <p className="text-sm text-destructive">
                  {toggleMut.error instanceof NotificationSettingsApiError
                    ? toggleMut.error.code
                    : 'Save failed'}
                </p>
              ) : null}
            </div>
          )}
        </PlanGate>
      </section>

      {/* Template wording editor — large plan only */}
      <section>
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
          Customise wording
        </h2>
        <PlanGate slug={slug} requiredPlan="large-business">
          {listQ.isLoading ? (
            <div className="h-64 animate-pulse rounded-2xl bg-secondary" />
          ) : listQ.error ? (
            <p className="text-sm text-destructive">
              Couldn&rsquo;t load templates.
            </p>
          ) : (
            <div className="space-y-4">
              {(listQ.data?.templates ?? []).map((row) => (
                <section
                  key={row.key}
                  className="rounded-2xl border border-brand-sage/15 bg-card p-5 shadow-brand"
                >
                  <h2 className="text-base font-semibold">
                    {KEY_LABELS[row.key] ?? row.key}
                  </h2>
                  <p className="text-xs text-muted-foreground">{row.key}</p>
                  <div className="mt-4 grid gap-2 sm:grid-cols-3">
                    {row.channels.map((c) => (
                      <Link
                        key={c.channel}
                        to="/admin/$slug/templates/$key/$channel"
                        params={{ slug, key: row.key, channel: c.channel }}
                        className="group flex flex-col rounded-lg border border-brand-sage/15 bg-background p-3 hover:border-primary/40 hover:bg-secondary/40"
                      >
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                          <ChannelIcon channel={c.channel} />
                          <span>{channelLabel(c.channel)}</span>
                        </div>
                        <p className="mt-2 text-xs">
                          {c.hasOverride ? (
                            <span className="font-semibold text-primary">
                              Custom
                            </span>
                          ) : (
                            <span className="text-muted-foreground">
                              Using default
                            </span>
                          )}
                        </p>
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </PlanGate>
      </section>
    </main>
  );
}

function ToggleButton({
  label,
  icon,
  enabled,
  disabled,
  onChange,
}: {
  label: string;
  icon: React.ReactNode;
  enabled: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      disabled={disabled}
      aria-pressed={enabled}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
        enabled
          ? 'bg-primary/10 text-primary ring-1 ring-primary/30 hover:bg-primary/20'
          : 'bg-muted text-muted-foreground ring-1 ring-border hover:bg-muted/80'
      } disabled:cursor-not-allowed disabled:opacity-50`}
    >
      {icon}
      {label}
      <span className="ml-0.5">{enabled ? 'On' : 'Off'}</span>
    </button>
  );
}

function ChannelIcon({ channel }: { channel: Channel }) {
  if (channel === 'email_subject') return <Type className="h-3.5 w-3.5" />;
  if (channel === 'email_body') return <Mail className="h-3.5 w-3.5" />;
  return <MessageSquare className="h-3.5 w-3.5" />;
}

function channelLabel(channel: Channel): string {
  if (channel === 'email_subject') return 'Email subject';
  if (channel === 'email_body') return 'Email body';
  return 'SMS';
}
